import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import { RoundingDirection, RoundingUnitKind } from "@sinch/core/entities/serverEnums";
import { useFormMutationSubmit } from "@sinch/hooks/useFormMutationSubmit";
import { useMuiForm } from "@sinch/hooks/useMuiForm";
import { useSnackbar } from "notistack";
import { isNotNilOrEmpty } from "ramda-adjunct";
import React, { FC, Suspense, useCallback, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type {
  CreateRateRoundingMutation,
  CreateRateRoundingMutationVariables,
  EditRateRoundingMutation,
  EditRateRoundingMutationVariables,
} from "./queries.types";
import { NumberInput, TextInput, useMuiController } from "@sinch/components";
import { RatelistOption } from "@sinch/components/application/Rate/components/RatelistSelectInput/RatelistSelectInput";
import { type RoundingFormData, roundingFormSchema } from "@sinch/components/application/RateRounding/formSchema";
import { useRateRounding } from "@sinch/components/application/RateRounding/useRateRounding";
import { useRoundingLabels } from "@sinch/components/application/RateRounding/useRoundingLabels";
import { CollapsibleListCard } from "@sinch/components/CollapsibleListCard/CollapsibleListCard";
import { FormContainer } from "@sinch/components/form/FormContainer/FormContainer";
import { SelectInput } from "@sinch/components/form/SelectInput/SelectInput";
import { SwitchInput } from "@sinch/components/form/SwitchInput/SwitchInput";
import { LoadingWrapper } from "@sinch/components/LoadingWrapper/LoadingWrapper";

interface RoundingFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (id?: string) => void;
  roundingId?: string;
}

/**
 * Create / edit dialog of a rounding definition
 */
export const RoundingFormDialog: FC<RoundingFormDialogProps> = ({ open, onClose, onSave, roundingId }) => (
  <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
    <Suspense fallback={<LoadingWrapper on />}>
      <RoundingForm onClose={onClose} onSave={onSave} roundingId={roundingId} />
    </Suspense>
  </Dialog>
);

const RoundingForm: FC<Omit<RoundingFormDialogProps, "open">> = ({ onClose, onSave, roundingId }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { getDirectionLabel, getKindLabel } = useRoundingLabels();
  const { create, edit, rateRounding, loading } = useRateRounding({ id: roundingId, queryOptions: { fetchPolicy: "network-only" } });

  const defaultValues = useMemo<RoundingFormData | undefined>(
    () =>
      rateRounding
        ? {
            name: rateRounding.name,
            description: rateRounding.description ?? "",
            archived: rateRounding.archived,
            // the query result carries the generated GraphQL enum type, the form schema the runtime one; values match
            unitKind: rateRounding.unitKind as RoundingUnitKind,
            base: rateRounding.base,
            direction: rateRounding.direction as RoundingDirection,
            grace: rateRounding.grace ?? null,
          }
        : undefined,
    [rateRounding]
  );

  const form = useMuiForm<RoundingFormData>({
    schema: roundingFormSchema,
    formOptions: { defaultValues, mode: "onChange", reValidateMode: "onChange", criteriaMode: "all" },
  });

  const submitCreate = useFormMutationSubmit<CreateRateRoundingMutation, CreateRateRoundingMutationVariables, RoundingFormData>({
    mutate: create,
    form,
    variableMapper: (values) => ({ input: values }),
  });
  const submitEdit = useFormMutationSubmit<EditRateRoundingMutation, EditRateRoundingMutationVariables, RoundingFormData>({
    mutate: edit,
    form,
    variableMapper: (values) => ({ id: roundingId!, input: values }),
  });

  const handleSubmit = useCallback(async () => {
    if (roundingId) {
      const response = await submitEdit();
      if (response?.editRateRounding.result) {
        onSave();
        onClose();
        enqueueSnackbar(t("RateRounding.saveSuccess"), { variant: "success" });
      }
      return;
    }
    const response = await submitCreate();
    if (response?.createRateRounding.result && response.createRateRounding.payload) {
      onSave(response.createRateRounding.payload);
      onClose();
      enqueueSnackbar(t("RateRounding.saveSuccess"), { variant: "success" });
    }
  }, [enqueueSnackbar, onClose, onSave, roundingId, submitCreate, submitEdit, t]);

  const settingsLocked = rateRounding?.modifiable === false;
  const direction = useWatch<RoundingFormData, "direction">({ name: "direction", control: form.control });
  const unitKind = useWatch<RoundingFormData, "unitKind">({ name: "unitKind", control: form.control });
  const grace = useWatch<RoundingFormData, "grace">({ name: "grace", control: form.control });
  // "up with grace period" without a grace period behaves exactly like plain "up", so it is most likely a slip;
  // a cleared field (NaN) counts as none, locked definitions cannot change anyway
  const graceValue = Number(grace ?? 0);
  const graceIsZero =
    direction === RoundingDirection.GraceUp && !settingsLocked && (graceValue === 0 || Number.isNaN(graceValue));
  const usedInRatelists = rateRounding?.ratelists ?? [];

  const { field: nameField } = useMuiController<RoundingFormData, "name">({ name: "name", control: form.control });
  const { field: descriptionField } = useMuiController<RoundingFormData, "description">({ name: "description", control: form.control });
  const { field: archivedField } = useMuiController<RoundingFormData, "archived">({ name: "archived", control: form.control });
  const { field: kindField } = useMuiController<RoundingFormData, "unitKind">({ name: "unitKind", control: form.control, disabled: settingsLocked });
  const { field: baseField } = useMuiController<RoundingFormData, "base">({ name: "base", control: form.control, disabled: settingsLocked });
  const { field: directionField } = useMuiController<RoundingFormData, "direction">({ name: "direction", control: form.control, disabled: settingsLocked });
  const { field: graceField } = useMuiController<RoundingFormData, "grace">({ name: "grace", control: form.control, disabled: settingsLocked });

  return (
    <FormContainer form={form}>
      <DialogTitle>{t("RateRounding.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {roundingId && <SwitchInput {...archivedField} label={t("RateRounding.archived")} />}
          {settingsLocked && <Alert severity="warning">{t("RateRounding.unableToChangeAlreadyUsed")}</Alert>}
          <TextInput {...nameField} fullWidth label={t("RateRounding.name")} />
          <TextInput {...descriptionField} fullWidth label={t("RateRounding.description")} multiline />
          <SelectInput
            {...kindField}
            fullWidth
            label={t("RateRounding.unitKind")}
            options={Object.values(RoundingUnitKind).map((kind) => ({ value: kind, label: getKindLabel(kind) }))}
          />
          <NumberInput
            {...baseField}
            fullWidth
            helperText={unitKind === RoundingUnitKind.Time ? t("RateRounding.baseHelpTime") : t("RateRounding.baseHelpDistance")}
            label={t("RateRounding.base")}
            min={0}
            onChange={(value) => baseField.onChange(value)}
            step={unitKind === RoundingUnitKind.Time ? 1 : "any"}
          />
          <SelectInput
            {...directionField}
            fullWidth
            label={t("RateRounding.direction")}
            options={Object.values(RoundingDirection).map((value) => ({ value, label: getDirectionLabel(value) }))}
          />
          {direction === RoundingDirection.GraceUp && (
            <NumberInput
              {...graceField}
              fullWidth
              helperText={t("RateRounding.graceHelp")}
              label={t("RateRounding.grace")}
              min={0}
              onChange={(value) => graceField.onChange(value)}
              step={unitKind === RoundingUnitKind.Time ? 1 : "any"}
              value={graceField.value ?? 0}
            />
          )}
          {graceIsZero && <Alert severity="warning">{t("RateRounding.graceZeroWarning")}</Alert>}
          {isNotNilOrEmpty(usedInRatelists) && (
            <CollapsibleListCard
              items={usedInRatelists.map((ratelist) => (
                <RatelistOption
                  key={ratelist.id}
                  adHoc={ratelist.adHoc}
                  includeType
                  name={ratelist.name}
                  status={ratelist.status}
                  type={ratelist.type}
                  validFrom={ratelist.validFrom}
                  validTo={ratelist.validTo}
                  version={ratelist.version}
                />
              ))}
              summaryText={t("RateRounding.usedInRatelistsCount", { count: usedInRatelists.length })}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} type="button">
          {t("cancel")}
        </Button>
        <Button loading={loading} onClick={handleSubmit} type="button" variant="contained">
          {t("Action.save")}
        </Button>
      </DialogActions>
    </FormContainer>
  );
};
